/*
 * Copyright 2026 Oddpods
 *
 * This Source Code Form is subject to the terms of the Mozilla Public
 * License, v. 2.0. If a copy of the MPL was not distributed with this
 * file, You can obtain one at https://mozilla.org/MPL/2.0/.
 *
 * See https://github.com/oddpods/cauce/blob/main/LICENSE
 */
package studio.oddpods.cauce.feature.patients

import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Button
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.unit.dp
import org.jetbrains.compose.resources.stringResource
import studio.oddpods.cauce.core.ui.asString
import studio.oddpods.cauce.feature.patients.generated.resources.Res
import studio.oddpods.cauce.feature.patients.generated.resources.patients_email_label
import studio.oddpods.cauce.feature.patients.generated.resources.patients_fee_label
import studio.oddpods.cauce.feature.patients.generated.resources.patients_fee_placeholder
import studio.oddpods.cauce.feature.patients.generated.resources.patients_name_label
import studio.oddpods.cauce.feature.patients.generated.resources.patients_notes_label
import studio.oddpods.cauce.feature.patients.generated.resources.patients_notes_placeholder
import studio.oddpods.cauce.feature.patients.generated.resources.patients_phone_label
import studio.oddpods.cauce.feature.patients.generated.resources.patients_phone_placeholder
import studio.oddpods.cauce.feature.patients.generated.resources.patients_save

@Composable
fun PatientFormContent(
    state: PatientFormState,
    onAction: (PatientFormAction) -> Unit,
    modifier: Modifier = Modifier,
) {
    Column(
        modifier = modifier
            .fillMaxWidth()
            .verticalScroll(rememberScrollState()),
    ) {
        Spacer(modifier = Modifier.height(8.dp))

        OutlinedTextField(
            value = state.name,
            onValueChange = { onAction(PatientFormAction.NameChanged(it)) },
            label = { Text(stringResource(Res.string.patients_name_label)) },
            singleLine = true,
            modifier = Modifier.fillMaxWidth(),
        )

        Spacer(modifier = Modifier.height(12.dp))

        OutlinedTextField(
            value = state.phone,
            onValueChange = { onAction(PatientFormAction.PhoneChanged(it)) },
            label = { Text(stringResource(Res.string.patients_phone_label)) },
            placeholder = { Text(stringResource(Res.string.patients_phone_placeholder)) },
            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Phone),
            singleLine = true,
            modifier = Modifier.fillMaxWidth(),
        )

        Spacer(modifier = Modifier.height(12.dp))

        OutlinedTextField(
            value = state.email,
            onValueChange = { onAction(PatientFormAction.EmailChanged(it)) },
            label = { Text(stringResource(Res.string.patients_email_label)) },
            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Email),
            singleLine = true,
            modifier = Modifier.fillMaxWidth(),
        )

        Spacer(modifier = Modifier.height(12.dp))

        OutlinedTextField(
            value = state.defaultFeeInput,
            onValueChange = { onAction(PatientFormAction.FeeChanged(it)) },
            label = { Text(stringResource(Res.string.patients_fee_label)) },
            placeholder = { Text(stringResource(Res.string.patients_fee_placeholder)) },
            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Decimal),
            singleLine = true,
            modifier = Modifier.fillMaxWidth(),
        )

        Spacer(modifier = Modifier.height(12.dp))

        OutlinedTextField(
            value = state.notes,
            onValueChange = { onAction(PatientFormAction.NotesChanged(it)) },
            label = { Text(stringResource(Res.string.patients_notes_label)) },
            placeholder = { Text(stringResource(Res.string.patients_notes_placeholder)) },
            minLines = 3,
            modifier = Modifier.fillMaxWidth(),
        )

        if (state.error != null) {
            Spacer(modifier = Modifier.height(8.dp))
            Text(state.error!!.asString(), color = MaterialTheme.colorScheme.error)
        }

        Spacer(modifier = Modifier.height(24.dp))

        Button(
            onClick = { onAction(PatientFormAction.SaveClicked) },
            enabled = !state.isSaving,
            modifier = Modifier.fillMaxWidth(),
        ) {
            Text(stringResource(Res.string.patients_save))
        }

        Spacer(modifier = Modifier.height(16.dp))
    }
}
